import React from 'react';
import { motion } from 'framer-motion';
import { Award, Zap, CheckCircle2, TrendingUp, Shield } from 'lucide-react';
import './Programs.css';
import SEO from '../components/SEO';

const ProgramsPage = () => {
    const pathways = [
        {
            age: '5–10 Years',
            title: 'Foundation Level',
            focus: 'Basic motor skills, fundamental sports techniques, and fun-based competition designed to ignite a lifelong passion for athletics.',
            features: ['Motor Skill Development', 'Intro to Teamwork', 'Game Fundamentals', 'Character Building']
        },
        {
            age: '11–15 Years',
            title: 'Development Level',
            focus: 'Position-specific training, tactical awareness, and competitive preparation for provincial-level excellence.',
            features: ['Tactical Drills', 'Endurance Building', 'Match Scenarios', 'Mental Toughness']
        },
        {
            age: '16–21 Years',
            title: 'High Performance',
            focus: 'Elite-level conditioning, professional pathways, and provincial/national selection preparation with advanced coaching.',
            features: ['Professional Pathways', 'Advanced Tactics', 'National Selection Prep', 'Career Mentorship']
        }
    ];

    return (
        <main className="programs-gallery">
            <SEO
                title="Programs & Pathways"
                description="Explore our elite sports training programs for ages 5-21. From foundation level skills to high-performance national selection preparation."
            />
            {/* Hero Section */}
            <section className="programs-gallery__hero" style={{
                backgroundImage: `linear-gradient(rgba(11, 28, 45, 0.8), rgba(11, 28, 45, 0.8)), url(${require('../Images/programs-hero.jpeg')})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
            }}>
                <div className="container">
                    <motion.span
                        className="programs-gallery__eyebrow"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                    >
                        Elite Training Pathways
                    </motion.span>
                    <motion.h1
                        className="programs-gallery__title"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                    >
                        FUEL YOUR <span className="programs-gallery__title--accent">AMBITION</span>
                    </motion.h1>
                    <motion.p
                        className="programs-gallery__subtitle"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                    >
                        From beginner fundamentals to national elite performance, we provide the structured coaching required to excel
                    </motion.p>
                </div>
            </section>

            {/* Pathway Cards Grid */}
            <section className="programs-gallery__grid">
                <div className="programs-gallery__cards">
                    {pathways.map((path, idx) => (
                        <motion.article
                            key={path.title}
                            className="programs-gallery__card"
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: idx * 0.15, duration: 0.6 }}
                        >
                            <span className="programs-gallery__age-badge">{path.age}</span>
                            <h3 className="programs-gallery__card-title">{path.title}</h3>
                            <p className="programs-gallery__card-description">{path.focus}</p>

                            <ul className="programs-gallery__features">
                                {path.features.map(f => (
                                    <li key={f} className="programs-gallery__feature-item">
                                        <CheckCircle2 size={20} className="programs-gallery__feature-icon" />
                                        {f}
                                    </li>
                                ))}
                            </ul>

                            <motion.a
                                href={`https://wa.me/263781665264?text=Hi, I would like to enroll my child in the ${encodeURIComponent(path.title)} (${path.age}) program.`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="programs-gallery__cta"
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                Enroll Child
                            </motion.a>
                        </motion.article>
                    ))}
                </div>
            </section>

            {/* Mentorship Section */}
            <section className="programs-gallery__mentorship">
                <div className="programs-gallery__mentorship-content">
                    <motion.div
                        className="programs-gallery__mentorship-text"
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                    >
                        <h2 className="programs-gallery__mentorship-title">
                            MENTORSHIP & <span className="programs-gallery__mentorship-title--accent">CERTIFICATION</span>
                        </h2>
                        <p className="programs-gallery__mentorship-description">
                            We don't just build athletes; we build leaders. Our programs include pathways for coaching development,
                            referee certifications, and career mentorship within the sports industry.
                        </p>

                        <div className="programs-gallery__mentorship-features">
                            <div className="programs-gallery__mentorship-feature">
                                <Award size={24} />
                                Professional Mentorship
                            </div>
                            <div className="programs-gallery__mentorship-feature">
                                <Zap size={24} />
                                Career Pathways
                            </div>
                            <div className="programs-gallery__mentorship-feature">
                                <Shield size={24} />
                                Certification Programs
                            </div>
                            <div className="programs-gallery__mentorship-feature">
                                <TrendingUp size={24} />
                                Leadership Development
                            </div>
                        </div>
                    </motion.div>

                    <motion.div
                        className="programs-gallery__mentorship-image-wrapper"
                        initial={{ opacity: 0, x: 30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                    >
                        <div className="programs-gallery__mentorship-border"></div>
                        <img
                            src="https://images.unsplash.com/photo-1540747913346-19e32dc3e97e?auto=format&fit=crop&q=80&w=800"
                            alt="Coach Mentoring"
                            className="programs-gallery__mentorship-image"
                        />
                    </motion.div>
                </div>
            </section>
        </main>
    );
};

export default ProgramsPage;
